import React, { useMemo } from 'react'
import { Column, useFlexLayout, useSortBy, useTable } from 'react-table'
import { Button, ButtonGroup, Flexbox, Icon, Table } from '@components/common'
import { DeprecProdUnit } from '@api/models'
import moment from 'moment'

interface ProductionUnitsTableProps {
  productionUnits: DeprecProdUnit[]
  allProductionUnits: DeprecProdUnit[]
  onEdit: (unit: DeprecProdUnit) => void
  onDelete: (unit: DeprecProdUnit) => void
  readonly?: boolean
  canEdit?: boolean
}

const ProductionUnitsTable: React.FC<ProductionUnitsTableProps> = ({
  productionUnits,
  allProductionUnits,
  onEdit,
  onDelete,
  readonly = false,
  canEdit = true,
}) => {
  const absoluteLatestRecord = useMemo(() => {
    if (allProductionUnits.length === 0) return null

    return allProductionUnits.reduce((latest, current) => {
      if (current.FiscalYear > latest.FiscalYear) {
        return current
      } else if (
        current.FiscalYear === latest.FiscalYear &&
        current.FiscalPeriod > latest.FiscalPeriod
      ) {
        return current
      }
      return latest
    })
  }, [allProductionUnits])

  const columns = useMemo<Column<DeprecProdUnit>[]>(
    () => [
      {
        Header: 'Fiscal Year',
        accessor: 'FiscalYear',
        Cell: ({ value }: { value: number }) => value,
      },
      {
        Header: 'Fiscal Period',
        accessor: 'FiscalPeriod',
        Cell: ({ value }: { value: number }) => value,
      },
      {
        Header: 'Period End Date',
        accessor: 'PeriodEndDate',
        Cell: ({ value }: { value: string }) => moment(value).format('MMM DD, YYYY'),
      },
      {
        Header: 'Units Produced',
        accessor: 'UnitsProduced',
        Cell: ({ value }: { value: number }) => value.toLocaleString(),
      },
      {
        Header: '',
        id: 'actions',
        disableSortBy: true,
        Cell: ({ row }: { row: { original: DeprecProdUnit } }) => {
          const isAbsoluteLatestRecord =
            absoluteLatestRecord &&
            row.original.FiscalYear === absoluteLatestRecord.FiscalYear &&
            row.original.FiscalPeriod === absoluteLatestRecord.FiscalPeriod

          const buttons = [
            <Button
              key="edit"
              icon={<Icon name="Edit" />}
              size="compact"
              onClick={e => {
                e.stopPropagation()
                onEdit(row.original)
              }}
            >
              Edit
            </Button>,
          ]

          if (isAbsoluteLatestRecord) {
            buttons.unshift(
              <Button
                key="delete"
                icon={<Icon name="Trash2" />}
                size="compact"
                intent="danger"
                onClick={e => {
                  e.stopPropagation()
                  onDelete(row.original)
                }}
              >
                Delete
              </Button>
            )
          }

          return (
            <Flexbox
              alignItems="center"
              justifyContent="flex-end"
              style={{ width: '100%' }}
            >
              <ButtonGroup hidden={!canEdit || readonly}>{buttons}</ButtonGroup>
            </Flexbox>
          )
        },
      },
    ],
    [canEdit, readonly, onEdit, onDelete, absoluteLatestRecord]
  )

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data: productionUnits,
      initialState: {
        sortBy: [{ id: 'FiscalYear', desc: false }],
      },
    },
    useFlexLayout,
    useSortBy
  )

  if (productionUnits.length === 0) {
    return <Table.NoResults text="No production units found" />
  }

  return (
    <Table
      {...getTableProps()}
      aria-label="Production Units Table"
      label="Production Units Table"
      aria-colcount={columns.length}
      aria-rowcount={productionUnits.length}
      height="550px"
    >
      <Table.Head sticky>
        {headerGroups.map((headerGroup, i) => (
          <Table.HeadRow {...headerGroup.getHeaderGroupProps()} key={i}>
            {headerGroup.headers.map((column, j) => (
              <Table.Header
                {...column.getHeaderProps(column.getSortByToggleProps())}
                column={column}
                key={j}
              >
                {column.render('Header')}
              </Table.Header>
            ))}
          </Table.HeadRow>
        ))}
      </Table.Head>

      <Table.Body {...getTableBodyProps()}>
        {rows.map((row, i) => {
          prepareRow(row)
          return (
            <Table.Row
              {...row.getRowProps()}
              onClick={() => {
                if (canEdit && !readonly) {
                  onEdit(row.original)
                }
              }}
              tabIndex={0}
              key={i}
            >
              {row.cells.map(cell => (
                <Table.Cell
                  {...cell.getCellProps()}
                  column={cell.column}
                  key={cell.column.id}
                >
                  {cell.render('Cell')}
                </Table.Cell>
              ))}
            </Table.Row>
          )
        })}
      </Table.Body>
    </Table>
  )
}

export default ProductionUnitsTable
